import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import Logo from "@/components/Logo";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) { toast.error("Use at least 8 characters"); return; }
    if (pwd !== confirm) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      setDone(true);
      toast.success("Password updated — please sign in.");
      setTimeout(() => nav("/login"), 1600);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="reset-page">
      <Card className="w-full max-w-md p-8 border-slate-200 shadow-none">
        <div className="mb-6"><Logo size="md" /></div>
        <Link to="/login" className="text-xs text-slate-500 font-mono-plex inline-flex items-center gap-1 mb-4"><ArrowLeft className="w-3 h-3" /> Back to sign in</Link>
        <div className="mb-6">
          <div className="text-xs font-mono-plex uppercase tracking-widest text-slate-500 mb-2">Set new password</div>
          <h2 className="font-heading text-3xl font-bold">Choose a new password</h2>
          <p className="text-sm text-slate-500 mt-2">Minimum 8 characters. You'll be signed in with the new one.</p>
        </div>
        {!token && (
          <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-3 mb-4">
            Missing or invalid reset token. Please request a new reset link.
          </div>
        )}
        {done ? (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 flex items-center gap-2" data-testid="reset-done">
            <CheckCircle2 className="w-4 h-4" /> Password updated. Redirecting to sign in…
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="pwd">New password</Label>
              <Input id="pwd" data-testid="reset-pwd" type="password" required value={pwd} onChange={(e) => setPwd(e.target.value)} className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="confirm">Confirm password</Label>
              <Input id="confirm" data-testid="reset-confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5" />
            </div>
            <Button type="submit" disabled={busy || !token} data-testid="reset-submit" className="w-full bg-slate-900 hover:bg-slate-800">
              {busy ? "Updating…" : "Update password"}
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
}
